import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    saveAccessToken,
    saveUserData,
    saveConnectionId,
} from "../utils/storage";

/**
 * EmbedLogin – Auto-authentication page for embedded widget access.
 *
 * Route: /embed?token=xxx
 *
 * Flow:
 * 1. Reads the embed token from the URL query string
 * 2. Calls POST /v1/embed/verify-token to validate it
 * 3. On success: saves auth data to sessionStorage and redirects to /home
 * 4. On failure: shows a clean error message
 */
const EmbedLogin: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<"loading" | "error">("loading");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        const token = searchParams.get("token");

        if (!token) {
            setStatus("error");
            setErrorMessage("No embed token provided");
            return;
        }

        const verifyToken = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/v1/embed/verify-token`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ token }),
                    }
                );

                if (!response.ok) {
                    const data = await response.json().catch(() => ({}));
                    throw new Error(data.detail || "Invalid or expired embed token");
                }

                const data = await response.json();

                // Save auth data to sessionStorage (same keys the app expects)
                saveAccessToken(data.access_token);
                saveUserData(data.user);

                if (data.connection_id) {
                    saveConnectionId(data.connection_id);
                }

                // Navigate to the embed dashboard (widget-only view)
                navigate("/embed/dashboard", { replace: true });
            } catch (err: any) {
                console.error("[EmbedLogin] Verification failed:", err);
                setStatus("error");
                setErrorMessage(err.message || "Failed to authenticate");
            }
        };

        verifyToken();
    }, [searchParams, navigate]);

    // ── Loading state ──
    if (status === "loading") {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <div style={styles.spinner}></div>
                    <p style={styles.text}>Authenticating...</p>
                </div>
            </div>
        );
    }

    // ── Error state ──
    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <div style={styles.errorIcon}>✕</div>
                <h2 style={styles.errorTitle}>Access Denied</h2>
                <p style={styles.errorText}>{errorMessage}</p>
                <p style={styles.hint}>
                    Please contact your administrator for a valid embed link.
                </p>
            </div>
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    container: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16162a 100%)",
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
    },
    card: {
        textAlign: "center",
        padding: "48px 40px",
        borderRadius: "20px",
        background: "rgba(255, 255, 255, 0.03)",
        border: "1px solid rgba(255, 255, 255, 0.06)",
        backdropFilter: "blur(20px)",
        maxWidth: "400px",
    },
    spinner: {
        width: "48px",
        height: "48px",
        border: "3px solid rgba(99, 102, 241, 0.2)",
        borderTopColor: "#6366f1",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
        margin: "0 auto 20px",
    },
    text: {
        color: "#94a3b8",
        fontSize: "15px",
        margin: 0,
    },
    errorIcon: {
        width: "56px",
        height: "56px",
        borderRadius: "50%",
        background: "rgba(239, 68, 68, 0.1)",
        border: "2px solid rgba(239, 68, 68, 0.3)",
        color: "#ef4444",
        fontSize: "24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 20px",
    },
    errorTitle: {
        color: "#e2e8f0",
        fontSize: "20px",
        fontWeight: 600,
        margin: "0 0 8px",
    },
    errorText: {
        color: "#ef4444",
        fontSize: "14px",
        margin: "0 0 16px",
    },
    hint: {
        color: "#64748b",
        fontSize: "13px",
        margin: 0,
    },
};

// Inject spinner animation globally
const styleTag = document.createElement("style");
styleTag.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(styleTag);

export default EmbedLogin;
